import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import {
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfCheckboxField,
  FrVrfDropdownField,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfPanel,
  FrVrfSingleLineDisplayField,
  FrVrfSingleLineInputField,
} from "@vrf/fr-vrf-web-components-react";
import { stringify } from "../../util/querystring-util";
import React, { useEffect, useMemo } from "react";
import { useNavigate, useLocation, createPath } from "react-router-dom";
import {
  ChnType,
  ChnTypeSpreadsheetRow,
  PartnerInfo,
  SpreadsheetState,
  usePageState,
} from ".";
import { useAppState } from "../../app";
import { ApplicationMessageScreen } from "../../messages";
import { useGetDipByRegionList } from "../../services/data-list";
import {
  useGetEdiChainTypeList,
  useGetGeneralCodes,
} from "../../services/data-list/DataListHook";
import {
  GetPartnerRequest,
  useCreatePartner,
  useGetPartner,
  useUpdatePartner,
} from "../../services/partner";
import { useGetRootComponents } from "../../services/root-components";
import { GLOBAL_ERROR } from "../../services/use-rest-resource-hook";
import {
  InDefCommonFlagType,
  IoType,
  PtnrBuCntryType,
  PtnrType,
} from "../../util/general-code/enums/general-codes";
import { Option } from "../../util/option";
import {
  INIT_ERROR_RESULT_DATA,
  clearBackenddMessages,
  mappingErrorResponseToSpreadsheet,
} from "../../util/spreadsheet-data-utils";
import { usePageNavigation } from "../use-page-navigation";
import {
  PartnerDetailMode,
  REQUEST_CD_TYPES,
  TEMPLATE_FILE_URL,
  createPartnerInfo,
  requestCdTypes,
} from "./PartnerDetailPageConstant";
import _ from "lodash";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";
require("./PartnerDetailContent.css");

export const PartnerDetailPageContent = () => {
  const transition = (path: string) => {
    const href = createPath({
      pathname: `${path}`,
    });
    navigate(href);
  };
  const navigate = useNavigate();
  const { resetUserError, showToast, showConfirmationDialog, errorMessages } =
    useGlobalNotifier();
  const { isModalPage } = usePageNavigation();
  const [appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();
  const { searchResult, dataList, fieldPath } = pageState;
  const { ptnrInfo } = searchResult;
  const { messageForScreen } = useMessage();

  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const params = {
    mode: queryParameter.get("mode") ?? "",
    ptnrId: queryParameter.get("partnerId") ?? "",
    godFlg: queryParameter.get("godFlg") ?? InDefCommonFlagType.NO,
  };

  const isGodMode: boolean = params.godFlg === InDefCommonFlagType.YES;

  const [getEdiChainTypeListResponse, sendGetEdiChainTypeListRequest] =
    useGetEdiChainTypeList();
  const [getGeneralCodesResponse, sendGetGeneralCodesRequest] =
    useGetGeneralCodes();
  const [getCreatePartnerResponse, sendCreatePartnerRequest] =
    useCreatePartner();
  const [getUpdatePartnerResponse, sendUpdatePartnerRequest] =
    useUpdatePartner();
  const [getGetPartnerResponse, sendGetPartnerRequest] = useGetPartner();
  const [getDipByRegionListResponse, sendGetDipByRegionListRequest] =
    useGetDipByRegionList();
  const [getGetRootComponentsResponse, sendGetRootComponentsRequest] =
    useGetRootComponents();

  /**
   * Inbound Spreadsheet State
   */
  const [
    inboundSpreadsheetState,
    setInboundSpreadsheetState,
    initInboundSpreadsheetState,
  ] = useSpreadsheetState<SpreadsheetState>();
  const inboundSpreadsheetInitialData = useMemo<SpreadsheetState>(() => {
    return {
      chnTypeList: pageState.searchResult.ptnrInfo.chnTypeList
        .filter((e) => e.inOutTypeCd === IoType.INBOUND)
        .map((e) => ({
          ...INIT_ERROR_RESULT_DATA,
          ...e,
        })),
      inDefCommonFlagType: dataList.inDefCommonFlagType,
      fileTypeType: dataList.fileTypeType,
      targetEdiChainType: dataList.inboundEdiChainType,
      inOutTypeCd: IoType.INBOUND,
      godFlg: params.godFlg ?? InDefCommonFlagType.NO,
    };
  }, [
    pageState.searchResult.ptnrInfo.chnTypeList,
    dataList.inDefCommonFlagType,
    dataList.fileTypeType,
    dataList.inboundEdiChainType,
  ]);

  /**
   * Outbound Spreadsheet State
   */
  const [
    outboundSpreadsheetState,
    setOutboundSpreadsheetState,
    initOutboundSpreadsheetState,
  ] = useSpreadsheetState<SpreadsheetState>();
  const outboundSpreadsheetInitialData = useMemo<SpreadsheetState>(() => {
    return {
      chnTypeList: pageState.searchResult.ptnrInfo.chnTypeList
        .filter((e) => e.inOutTypeCd === IoType.OUTBOUND)
        .map((e) => ({
          ...INIT_ERROR_RESULT_DATA,
          ...e,
        })),
      inDefCommonFlagType: dataList.inDefCommonFlagType,
      fileTypeType: dataList.fileTypeType,
      targetEdiChainType: dataList.outboundEdiChainType,
      inOutTypeCd: IoType.OUTBOUND,
      godFlg: params.godFlg ?? InDefCommonFlagType.NO,
    };
  }, [
    pageState.searchResult.ptnrInfo.chnTypeList,
    dataList.inDefCommonFlagType,
    dataList.fileTypeType,
    dataList.outboundEdiChainType,
  ]);

  const onInitialDisplayed = () => {
    resetUserError();

    if (
      params.mode !== PartnerDetailMode.ADD &&
      params.mode !== PartnerDetailMode.MODIFY
    ) {
      showConfirmationDialog({
        ...GLOBAL_ERROR,
        onOkButtonClicked: () => {
          navigate(-1);
        },
      });
      return;
    }

    setAppState({
      ...appState,
      title:
        params.mode === PartnerDetailMode.ADD
          ? "Add Partner"
          : "Partner Detail",
      isGodMode,
    });
    sendGetEdiChainTypeListRequest({});

    sendGetGeneralCodesRequest({
      cdTypes: requestCdTypes,
    });
    if (params.mode === PartnerDetailMode.MODIFY) {
      sendGetPartnerRequest({ ptnrId: params.ptnrId });
    }
    sendGetDipByRegionListRequest({});
    sendGetRootComponentsRequest({});
  };

  const onGetEdiChainTypeListResponded = () => {
    if (!getEdiChainTypeListResponse) return;

    const inboundEdiChainType = getEdiChainTypeListResponse.ediChainTypeList
      .filter((e) => e.inOutTypeCd === IoType.INBOUND)
      .map((e) => ({
        label: e.chnTypeName,
        value: e.chnTypeCd,
        godOnlyFlag: e.godOnlyFlag,
      }));
    const outboundEdiChainType = getEdiChainTypeListResponse.ediChainTypeList
      .filter((e) => e.inOutTypeCd === IoType.OUTBOUND)
      .map((e) => ({
        label: e.chnTypeName,
        value: e.chnTypeCd,
        godOnlyFlag: e.godOnlyFlag,
      }));

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        inboundEdiChainType,
        outboundEdiChainType,
      },
    }));
  };

  const onGetGeneralCodesResponded = () => {
    if (!getGeneralCodesResponse) return;
    const fileTypeType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.FILE_TYPE_TYPE,
      )?.cdList ?? [];
    const ptnrType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.PTNR_TYPE,
      )?.cdList ?? [];
    const ptnrBuCntryType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.PTNR_BU_CNTRY_TYPE,
      )?.cdList ?? [];
    const inDefCommonFlagType =
      getGeneralCodesResponse.gnrlCdTypeList.find(
        (e) => e.cdType === REQUEST_CD_TYPES.IN_DEF_COMMON_FLAG_TYPE,
      )?.cdList ?? [];

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        fileTypeType,
        ptnrType,
        ptnrBuCntryType,
        inDefCommonFlagType,
      },
    }));
  };

  const onGetDipByRegionListResponded = () => {
    const dipByRegions = getDipByRegionListResponse?.dipByRgnList ?? [];
    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        dipByRgn: dipByRegions.map((e) => ({
          label: e.dipByRgn,
          value: e.dipByRgn,
        })),
      },
    }));
  };

  const onGetPartnerResponded = () => {
    const ptnrInfo: PartnerInfo = createPartnerInfo(getGetPartnerResponse);
    setPageState((prevState) => ({
      ...prevState,
      searchResult: {
        ...prevState.searchResult,
        ptnrInfo,
      },
    }));
  };

  const onCreatePartnerResponded = () => {
    if (getCreatePartnerResponse) {
      successToastMessage(ApplicationMessageScreen.APP_SCN_00044);
      const previousPage = isGodMode ? -2 : -1;
      navigate(previousPage);
    }
  };

  const onUpdatePartnerResponded = () => {
    if (!getUpdatePartnerResponse) {
      return;
    }

    if (Array.isArray(getUpdatePartnerResponse)) {
      const currentInboundRecords = inboundSpreadsheetState
        ? inboundSpreadsheetState.chnTypeList
        : [];
      const currentOutboundRecords = outboundSpreadsheetState
        ? outboundSpreadsheetState.chnTypeList
        : [];

      const currentrecords = [
        ...currentInboundRecords,
        ...currentOutboundRecords,
      ];

      const currentrecordsWithoutBackendError = currentrecords.map((row) =>
        clearBackenddMessages(row),
      );

      const newSpreadsheetRecordData: ChnTypeSpreadsheetRow[] =
        mappingErrorResponseToSpreadsheet(
          getUpdatePartnerResponse,
          currentrecordsWithoutBackendError,
          pageState.displayColumn,
        );

      const newInboundSpreadSheetData: ChnTypeSpreadsheetRow[] =
        newSpreadsheetRecordData.filter(
          (e) => e.inOutTypeCd === IoType.INBOUND,
        );
      const newOutboundSpreadSheetData: ChnTypeSpreadsheetRow[] =
        newSpreadsheetRecordData.filter(
          (e) => e.inOutTypeCd === IoType.OUTBOUND,
        );

      if (inboundSpreadsheetState)
        setInboundSpreadsheetState({
          ...inboundSpreadsheetState,
          chnTypeList: newInboundSpreadSheetData,
        });
      if (outboundSpreadsheetState)
        setOutboundSpreadsheetState({
          ...outboundSpreadsheetState,
          chnTypeList: newOutboundSpreadSheetData,
        });
    } else {
      successToastMessage(ApplicationMessageScreen.APP_SCN_00044);
      const previousPage = isGodMode ? -2 : -1;
      navigate(previousPage);
    }
  };

  const onGetRootComponentsResponded = () => {
    if (!getGetRootComponentsResponse) return;

    const targetDipRootCpntList =
      getGetRootComponentsResponse.rootCpntList.filter(
        (e) =>
          e.dipByRgn === searchResult.ptnrInfo.dipByRgn &&
          e.cpntList.length > 0,
      );

    const targetCpntList: Option[] = targetDipRootCpntList.flatMap((rootCpnt) =>
      rootCpnt.cpntList.map((cpnt) => ({
        label: cpnt.cpntId,
        value: cpnt.cpntId,
      })),
    );

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        cpntList: targetCpntList,
      },
    }));
  };

  const successToastMessage = (messageId: string) => {
    showToast({
      design: "completion",
      message: messageForScreen(messageId),
    });
  };

  useEffect(onInitialDisplayed, [params.mode, params.ptnrId, params.godFlg]);
  useEffect(onGetPartnerResponded, [getGetPartnerResponse]);
  useEffect(onGetEdiChainTypeListResponded, [getEdiChainTypeListResponse]);
  useEffect(onGetGeneralCodesResponded, [getGeneralCodesResponse]);
  useEffect(onUpdatePartnerResponded, [getUpdatePartnerResponse]);
  useEffect(onCreatePartnerResponded, [getCreatePartnerResponse]);
  useEffect(onGetDipByRegionListResponded, [getDipByRegionListResponse]);
  useEffect(onGetRootComponentsResponded, [
    getGetRootComponentsResponse,
    searchResult.ptnrInfo.dipByRgn,
  ]);

  const isBlankSearchArea = (ptnrInfo: PartnerInfo) => {
    if (!ptnrInfo.ptnrName) return true;
    if (!ptnrInfo.ptnrTypeCd) return true;
    if (!ptnrInfo.buCnTypeCd) return true;
    if (
      ptnrInfo.ptnrTypeCd === PtnrType.OTHERS &&
      ptnrInfo.buCnTypeCd === PtnrBuCntryType.BU &&
      !ptnrInfo.buCd
    )
      return true;
    if (
      ptnrInfo.ptnrTypeCd === PtnrType.OTHERS &&
      ptnrInfo.buCnTypeCd === PtnrBuCntryType.CN &&
      !ptnrInfo.cnCd
    )
      return true;
    if (!ptnrInfo.dipByRgn) return true;
    if (!ptnrInfo.cpntId) return true;
    return false;
  };

  const isBlankChnType = (chnType: ChnType) => {
    if (!chnType.chnTypeCd) return true;
    if (!chnType.chnTypeDelFlg) return true;
    if (!chnType.fileTypeCd) return true;
    return false;
  };

  const validate = useMemo(() => {
    return {
      searchArea: {
        isBlank: isBlankSearchArea(searchResult.ptnrInfo),
      },
      inbound: {
        isBlank:
          inboundSpreadsheetState?.chnTypeList.length !== 0 &&
          !!inboundSpreadsheetState?.chnTypeList.find((e) => isBlankChnType(e)),
      },
      outbound: {
        isBlank:
          outboundSpreadsheetState?.chnTypeList.length !== 0 &&
          !!outboundSpreadsheetState?.chnTypeList.find((e) =>
            isBlankChnType(e),
          ),
      },
    };
  }, [pageState, inboundSpreadsheetState, outboundSpreadsheetState]);

  const clickedSaveButtonHandle = () => {
    showConfirmationDialog({
      type: "confirm",
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00045),
      onOkButtonClicked: () => {
        resetUserError();
        if (params.mode === PartnerDetailMode.ADD) {
          const newInboundChnTypeList = inboundSpreadsheetState
            ? inboundSpreadsheetState.chnTypeList
            : [];
          const newOutboundChnTypeList = outboundSpreadsheetState
            ? outboundSpreadsheetState.chnTypeList
            : [];

          const newChnTypeList = [
            ...newInboundChnTypeList,
            ...newOutboundChnTypeList,
          ];

          sendCreatePartnerRequest({
            ptnrCd: ptnrInfo.ptnrCd,
            ptnrName: ptnrInfo.ptnrName,
            ptnrTypeCd: ptnrInfo.ptnrTypeCd,
            buCnTypeCd: ptnrInfo.buCnTypeCd,
            buCd: ptnrInfo.buCd,
            cnCd: ptnrInfo.cnCd,
            dipByRgn: ptnrInfo.dipByRgn,
            cpntId: ptnrInfo.cpntId,
            chnTypeList: newChnTypeList.map((e) => ({
              inOutTypeCd: e.inOutTypeCd,
              chnTypeCd: e.chnTypeCd,
              fileTypeCd: e.fileTypeCd,
            })),
          });
        } else {
          const newInboundChnTypeList = inboundSpreadsheetState
            ? inboundSpreadsheetState.chnTypeList
            : [];
          const newOutboundChnTypeList = outboundSpreadsheetState
            ? outboundSpreadsheetState.chnTypeList
            : [];

          const newChnTypeList = [
            ...newInboundChnTypeList,
            ...newOutboundChnTypeList,
          ];

          sendUpdatePartnerRequest({
            ptnrId: ptnrInfo.ptnrId,
            ptnrCd: ptnrInfo.ptnrCd,
            ptnrName: ptnrInfo.ptnrName,
            ptnrNameWtngChng: ptnrInfo.ptnrNameWtngChng,
            ptnrTypeCd: ptnrInfo.ptnrTypeCd,
            buCnTypeCd: ptnrInfo.buCnTypeCd,
            buCd: ptnrInfo.buCd,
            cnCd: ptnrInfo.cnCd,
            dipByRgn: ptnrInfo.dipByRgn,
            cpntId: ptnrInfo.cpntId,
            delFlg: ptnrInfo.delFlg,
            updtDtimePtnr: ptnrInfo.updtDtimePtnr,
            chnTypeList: newChnTypeList.map((e, idx) => ({
              index: idx,
              chnTypeDelFlg: e.chnTypeDelFlg,
              inOutTypeCd: e.inOutTypeCd,
              chnTypeCd: e.chnTypeCd,
              fileTypeCd: e.fileTypeCd,
              updtDtimePtnrChnType: e.updtDtimePtnrChnType,
            })),
          });
        }
      },
    });
  };

  const valueChangeHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    const isValid = event.detail.isValid;

    if (name === "buCnTypeCd") {
      const buCd = value === PtnrBuCntryType.BU ? ptnrInfo.buCd : "";
      const cnCd = value === PtnrBuCntryType.CN ? ptnrInfo.cnCd : "";

      setPageState((prevState) => ({
        ...prevState,
        searchResult: {
          ...prevState.searchResult,
          ptnrInfo: {
            ...prevState.searchResult.ptnrInfo,
            buCd,
            cnCd,
            [name]: value,
          },
        },
      }));
      return;
    }

    setPageState((prevState) => ({
      ...prevState,
      searchResult: {
        ...prevState.searchResult,
        ptnrInfo: {
          ...prevState.searchResult.ptnrInfo,
          [name]: value,
        },
      },
      formValidation: {
        ...prevState.formValidation,
        [name]: isValid,
      },
    }));
  };

  const onChangeCheckboxHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    setPageState((prevState) => ({
      ...prevState,
      manual: value,
    }));
  };

  /**
   * Footer
   */
  const onClickCancelButton = () => {
    if (validate.searchArea.isBlank) {
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        onOkButtonClicked: () => {
          navigate(-1);
        },
      });
      return;
    }
    navigate(-1);
  };

  /**
   * Add Row Button Clicked.
   * @param event that is passed from spreadsheet.
   */
  const onClickAddRow = (inOutType: IoType) => () => {
    if (!inboundSpreadsheetState || !outboundSpreadsheetState) return;
    const targetSpreadSheet =
      inOutType === IoType.INBOUND
        ? inboundSpreadsheetState
        : outboundSpreadsheetState;

    const targetSelectedRowIndex =
      inOutType === IoType.INBOUND
        ? pageState.inboundSpreadsheetSelectedRowIndex
        : pageState.outboundSpreadsheetSelectedRowIndex;

    const newSpreadsheetState: ChnTypeSpreadsheetRow[] = targetSpreadSheet
      ? [...targetSpreadSheet.chnTypeList]
      : [];

    const insertRowIndex: number = (targetSelectedRowIndex ?? 0) + 1;
    const addSpreadsheetRow: ChnTypeSpreadsheetRow = {
      addRowFlg: InDefCommonFlagType.YES,
      chnTypeDelFlg: InDefCommonFlagType.NO,
      inOutTypeCd: inOutType,
      inOutTypeName: "",
      chnTypeCd: "",
      chnTypeName: "",
      fileTypeCd: "",
      fileTypeName: "",
      updtDtimePtnrChnType: "",
      errorColumnList: [],
      errorMessage: "",
      messageList: [],
    };

    newSpreadsheetState.splice(insertRowIndex, 0, addSpreadsheetRow);

    if (inOutType === IoType.INBOUND) {
      setInboundSpreadsheetState({
        ...inboundSpreadsheetState,
        chnTypeList: newSpreadsheetState,
      });
    }
    if (inOutType === IoType.OUTBOUND) {
      setOutboundSpreadsheetState({
        ...outboundSpreadsheetState,
        chnTypeList: newSpreadsheetState,
      });
    }
  };

  const onCellEdited = (inOutType: IoType) => (event: CustomEvent) => {
    if (!inboundSpreadsheetState || !outboundSpreadsheetState) return;

    const targetSpreadSheet =
      inOutType === IoType.INBOUND
        ? inboundSpreadsheetState
        : outboundSpreadsheetState;
    const copyChnTypeList: ChnTypeSpreadsheetRow[] = targetSpreadSheet
      ? JSON.parse(JSON.stringify(targetSpreadSheet.chnTypeList))
      : [];

    const rowIndex: number = event.detail.cell.data.index;
    const value: string = event.detail.cell.value ?? "";
    const eventName: Exclude<
      keyof ChnType,
      "errorColumnList" | "errorMessage" | "messageList"
    > = event.detail.name;

    const newRow = copyChnTypeList[rowIndex];

    newRow[eventName] = value;

    if (inOutType === IoType.INBOUND) {
      setInboundSpreadsheetState({
        ...inboundSpreadsheetState,
        chnTypeList: copyChnTypeList,
      });
    }
    if (inOutType === IoType.OUTBOUND) {
      setOutboundSpreadsheetState({
        ...outboundSpreadsheetState,
        chnTypeList: copyChnTypeList,
      });
    }
  };

  const onCellBulkEdited = (inOutType: IoType) => (event: CustomEvent) => {
    if (!inboundSpreadsheetState || !outboundSpreadsheetState) return;
    const targetSpreadSheet =
      inOutType === IoType.INBOUND
        ? inboundSpreadsheetState
        : outboundSpreadsheetState;
    const newChnTypList = _.cloneDeep(
      targetSpreadSheet?.chnTypeList,
    ) as ChnTypeSpreadsheetRow[];

    const eventName: Exclude<
      keyof ChnType,
      "errorColumnList" | "errorMessage" | "messageList"
    > = event.detail.name;

    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const value: string = item.value;
      const rowIdx: number = item.data.index;

      newChnTypList[rowIdx][eventName] = value;
    });

    if (inOutType === IoType.INBOUND) {
      setInboundSpreadsheetState({
        ...inboundSpreadsheetState,
        chnTypeList: newChnTypList,
      });
    }
    if (inOutType === IoType.OUTBOUND) {
      setOutboundSpreadsheetState({
        ...outboundSpreadsheetState,
        chnTypeList: newChnTypList,
      });
    }
  };

  /**
   * Spreadsheet cell selected.
   * @param event that is passed from spreadsheet.
   */
  const onCellSelected = (inOutType: IoType) => (event: CustomEvent) => {
    if (!inboundSpreadsheetState || !outboundSpreadsheetState) return;

    const rowIndex: number = event.detail.cell?.data.index;

    if (inOutType === IoType.INBOUND) {
      setPageState((prevState) => ({
        ...prevState,
        inboundSpreadsheetSelectedRowIndex: rowIndex,
      }));
    }
    if (inOutType === IoType.OUTBOUND) {
      setPageState((prevState) => ({
        ...prevState,
        outboundSpreadsheetSelectedRowIndex: rowIndex,
      }));
    }
  };

  /**
   * Spreadsheet delete icon clicked.
   * @param event that is passed from spreadsheet.
   */
  const onDeleteIconClicked = (inOutType: IoType) => (event: CustomEvent) => {
    if (event.detail.name !== "deleteRow") return;
    if (!inboundSpreadsheetState || !outboundSpreadsheetState) return;
    const targetSpreadSheet =
      inOutType === IoType.INBOUND
        ? inboundSpreadsheetState
        : outboundSpreadsheetState;

    const newSpreadsheetState: ChnTypeSpreadsheetRow[] = targetSpreadSheet
      ? [...targetSpreadSheet.chnTypeList]
      : [];

    const rowIndex: number = event.detail.cell?.data.index;
    newSpreadsheetState.splice(rowIndex, 1);

    if (inOutType === IoType.INBOUND) {
      setInboundSpreadsheetState({
        ...inboundSpreadsheetState,
        chnTypeList: newSpreadsheetState,
      });
    }
    if (inOutType === IoType.OUTBOUND) {
      setOutboundSpreadsheetState({
        ...outboundSpreadsheetState,
        chnTypeList: newSpreadsheetState,
      });
    }
  };

  /**
   * GOD
   */
  const onClickGodConfirmDialogHandle = () => {
    showConfirmationDialog({
      title: "",
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00046),
      type: "warning",
      onOkButtonClicked: () => {
        const newParams = {
          mode: params.mode,
          partnerId: params.ptnrId,
          godFlg: InDefCommonFlagType.YES,
        };
        transition(`/partner-detail?${stringify(newParams)}`);
      },
    });
  };

  return (
    <>
      {(params.mode === PartnerDetailMode.ADD ||
        params.mode === PartnerDetailMode.MODIFY) && (
        <>
          {/* GOD */}
          {params.godFlg !== InDefCommonFlagType.YES && (
            <>
              <FrVrfButtonPanel>
                <FrVrfButton
                  content="IE Maintenance"
                  size="medium"
                  theme="caution"
                  onClicked={onClickGodConfirmDialogHandle}
                  authActId="partner_detail.button.god"
                />
              </FrVrfButtonPanel>
              <br />
            </>
          )}
          {/* Main Content */}
          <FrVrfFormPanel columns={2}>
            <div>
              <FrVrfFormPanel>
                <div>
                  {params.mode === PartnerDetailMode.ADD &&
                    pageState.manual && (
                      <>
                        <FrVrfSingleLineInputField
                          labelContent="Partner Code"
                          content={ptnrInfo.ptnrCd}
                          onInputValueChanged={valueChangeHandle("ptnrCd")}
                          required
                          constraint="[Size(value, {min:8 , max: 8}), IsNotEmpty(value), Pattern(value, {regex: '^[1-2]+\\d{7}', name:'Partner Code'})]"
                          errorMessage={errorMessages(
                            fieldPath.ptnrCd,
                            isModalPage(),
                          )}
                        />
                      </>
                    )}
                </div>
                <div>
                  {params.mode === PartnerDetailMode.ADD && (
                    <>
                      <FrVrfCheckboxField
                        labelContent="Manual"
                        checked={pageState.manual}
                        onCheckedChanged={onChangeCheckboxHandle("manual")}
                      />
                      <FrVrfSingleLineInputField
                        labelContent="Partner Name"
                        content={ptnrInfo.ptnrName}
                        onInputValueChanged={valueChangeHandle("ptnrName")}
                        required
                        constraint="[Size(value, {min:1 , max: 256}), IsNotEmpty(value)]"
                        errorMessage={errorMessages(
                          fieldPath.ptnrName,
                          isModalPage(),
                        )}
                      />
                    </>
                  )}
                </div>
                <div>
                  {params.mode === PartnerDetailMode.MODIFY && (
                    <>
                      <FrVrfSingleLineDisplayField
                        label={`Partner Code`}
                        firstValue={ptnrInfo.ptnrCd}
                      />
                      <FrVrfSingleLineDisplayField
                        label="Partner Name"
                        firstValue={ptnrInfo.ptnrName}
                      />
                      <FrVrfSingleLineInputField
                        labelContent="Partner Name (Waiting to Be Changed)"
                        content={ptnrInfo.ptnrNameWtngChng}
                        onInputValueChanged={valueChangeHandle(
                          "ptnrNameWtngChng",
                        )}
                        errorMessage={errorMessages(
                          fieldPath.ptnrNameWtngChng,
                          isModalPage(),
                        )}
                        constraint="[Size(value, {min: 0, max: 256})]"
                      />
                    </>
                  )}
                </div>

                <FrVrfSingleLineDisplayField
                  label="Partner Type"
                  firstValue={
                    dataList.ptnrType.find(
                      (e) => e.value === ptnrInfo.ptnrTypeCd,
                    )?.label
                  }
                />

                <div>
                  {ptnrInfo.ptnrTypeCd === PtnrType.OTHERS && (
                    <>
                      <FrVrfDropdownField
                        labelContent="BU / CN Type"
                        selectedValue={ptnrInfo.buCnTypeCd}
                        onValueChanged={valueChangeHandle("buCnTypeCd")}
                        items={dataList.ptnrBuCntryType}
                        required
                        disabled={ptnrInfo.ptnrTypeCd !== PtnrType.OTHERS}
                        constraint={`IsNotEmpty(${!ptnrInfo.buCnTypeCd})`}
                        includeDefaultValue={false}
                        errorMessage={errorMessages(
                          fieldPath.buCnTypeCd,
                          isModalPage(),
                        )}
                      />
                      <FrVrfSingleLineInputField
                        labelContent="BU Code"
                        content={ptnrInfo.buCd}
                        onInputValueChanged={valueChangeHandle("buCd")}
                        required={ptnrInfo.buCnTypeCd === PtnrBuCntryType.BU}
                        disabled={ptnrInfo.buCnTypeCd !== PtnrBuCntryType.BU}
                        constraint={
                          ptnrInfo.buCnTypeCd === PtnrBuCntryType.BU
                            ? "[Size(value, {min: 2, max: 2}), IsNotEmpty(value), IsNumericChar(value)]"
                            : ""
                        }
                        errorMessage={errorMessages(
                          fieldPath.buCd,
                          isModalPage(),
                        )}
                      />
                      <FrVrfSingleLineInputField
                        labelContent="CN Code"
                        content={ptnrInfo.cnCd}
                        onInputValueChanged={valueChangeHandle("cnCd")}
                        required={ptnrInfo.buCnTypeCd === PtnrBuCntryType.CN}
                        disabled={ptnrInfo.buCnTypeCd !== PtnrBuCntryType.CN}
                        constraint={
                          ptnrInfo.buCnTypeCd === PtnrBuCntryType.CN
                            ? "[Size(value, {min: 2, max: 2}), IsNotEmpty(value), IsNumericChar(value)]"
                            : ""
                        }
                        errorMessage={errorMessages(
                          fieldPath.cnCd,
                          isModalPage(),
                        )}
                      />
                    </>
                  )}
                </div>
                <div>
                  {params.mode === PartnerDetailMode.MODIFY &&
                    ptnrInfo.ptnrTypeCd !== PtnrType.OTHERS && (
                      <>
                        <FrVrfSingleLineDisplayField
                          label="BU / CN Type"
                          firstValue={ptnrInfo.buCnTypeName}
                        />
                        <FrVrfSingleLineDisplayField
                          label="BU Code"
                          firstValue={ptnrInfo.buCd}
                        />
                        <FrVrfSingleLineDisplayField
                          label="CN Code"
                          firstValue={ptnrInfo.cnCd}
                        />
                      </>
                    )}
                </div>

                <FrVrfDropdownField
                  labelContent="DIP By Region"
                  selectedValue={ptnrInfo.dipByRgn}
                  onValueChanged={valueChangeHandle("dipByRgn")}
                  items={dataList.dipByRgn}
                  required
                  errorMessage={errorMessages(
                    fieldPath.dipByRgn,
                    isModalPage(),
                  )}
                />
                <FrVrfDropdownField
                  labelContent="Component Id"
                  selectedValue={ptnrInfo.cpntId}
                  onValueChanged={valueChangeHandle("cpntId")}
                  items={dataList.cpntList}
                  required
                  errorMessage={errorMessages(fieldPath.cpntId, isModalPage())}
                />
              </FrVrfFormPanel>
            </div>
            <div>
              <FrVrfPanel panelTitle="Inbound">
                <FrVrfButtonPanel>
                  <FrVrfButton
                    content="Add Row"
                    theme="secondary"
                    size="small"
                    onClicked={onClickAddRow(IoType.INBOUND)}
                    authActId="partner_detail.button.inbound_add_row"
                  />
                </FrVrfButtonPanel>
                <FrVrfSpreadsheet
                  data={inboundSpreadsheetInitialData}
                  templateUrl={TEMPLATE_FILE_URL}
                  onInitialized={initInboundSpreadsheetState}
                  onCellSelected={onCellSelected(IoType.INBOUND)}
                  onCellClicked={onDeleteIconClicked(IoType.INBOUND)}
                  onCellEdited={onCellEdited(IoType.INBOUND)}
                  onCellBulkEdited={onCellBulkEdited(IoType.INBOUND)}
                  height={200}
                />
              </FrVrfPanel>
              <br />
              <FrVrfPanel panelTitle="Outbound">
                <FrVrfButtonPanel>
                  <FrVrfButton
                    content="Add Row"
                    theme="secondary"
                    size="small"
                    onClicked={onClickAddRow(IoType.OUTBOUND)}
                    authActId="partner_detail.button.outbound_add_row"
                  />
                </FrVrfButtonPanel>
                <FrVrfSpreadsheet
                  data={outboundSpreadsheetInitialData}
                  templateUrl={TEMPLATE_FILE_URL}
                  onInitialized={initOutboundSpreadsheetState}
                  onCellSelected={onCellSelected(IoType.OUTBOUND)}
                  onCellClicked={onDeleteIconClicked(IoType.OUTBOUND)}
                  onCellEdited={onCellEdited(IoType.OUTBOUND)}
                  onCellBulkEdited={onCellBulkEdited(IoType.OUTBOUND)}
                  height={200}
                />
              </FrVrfPanel>
            </div>
          </FrVrfFormPanel>
          {/* Footer */}
          <FrVrfFooter className="footer">
            {/* Cancel Button  */}
            <FrVrfButton
              content="Cancel"
              theme="secondary"
              size="large"
              onClicked={onClickCancelButton}
            />
            {/* Save Button */}
            <FrVrfButton
              content="Save"
              theme="primary"
              size="large"
              onClicked={clickedSaveButtonHandle}
              disabled={
                validate.searchArea.isBlank ||
                Object.values(pageState.formValidation).some(
                  (isValid) => isValid === false,
                )
              }
              authActId="partner_detail.button.save"
            />
          </FrVrfFooter>
        </>
      )}
    </>
  );
};
